import React, { useCallback, useRef, useState } from 'react';

import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import type { ContentProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { CuratedPalette } from './curated-palette';
import { CustomPalette, type CustomPaletteProps } from './custom-palette';
import { messages } from './messages';
import { ColorValidationMessages } from './validation-messages';

const containerStyles = xcss({
	flexDirection: 'column',
	gap: 'space.200',
	width: '292px',
});

const headerStyles = xcss({
	alignItems: 'center',
	alignSelf: 'stretch',
	justifyContent: 'space-between',
});

const resetButtonStyles = xcss({
	paddingTop: 'space.050',
});

type ColorPickerDropdownProps = ContentProps &
	Pick<CustomPaletteProps, 'builtInColorMap' | 'value' | 'onChange' | 'analyticsSource'> & {
		enableBorderLessStyle?: boolean;
		isReadableColor?: (color: string) => boolean;
		pickerTitle?: MessageDescriptor;
		showCloseButton?: boolean;
		showCustomPalette: boolean;
		showResetButton?: boolean;
		validationFailMessage?: string;
		validationSuccessMessage?: string;
	};

export const ColorPickerDropdown = ({
	analyticsSource,
	builtInColorMap,
	isReadableColor,
	onChange,
	onClose,
	value,
	pickerTitle,
	showCustomPalette,
	showResetButton = false,
	showCloseButton = false,
	validationSuccessMessage,
	validationFailMessage,
	enableBorderLessStyle = false,
	...props
}: ColorPickerDropdownProps) => {
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [preSelectedColor, setPreSelectedColor] = useState<string>(value);
	const customColorRef = useRef<typeof value>();

	const onCuratedColorSelect = useCallback(
		(color: string) => {
			onChange(color);
			setPreSelectedColor(color);
		},
		[onChange],
	);

	const onPreSelectCustomColor = useCallback(
		(color: string) => {
			setPreSelectedColor(color);
			setShowConfirmation(false);
		},
		[setPreSelectedColor, setShowConfirmation],
	);

	const onAddCustomColor = useCallback(
		(color: string) => {
			onChange(color);
			setPreSelectedColor(color);
			setShowConfirmation(true);
		},
		[onChange],
	);

	const onResetClick = useCallback(() => {
		onChange(undefined);
		setShowConfirmation(false);
	}, [onChange]);

	return (
		<Flex {...props} xcss={containerStyles}>
			<Stack space="space.150">
				<Flex xcss={headerStyles}>
					<Text color="color.text" size="small" weight="bold">
						<FormattedMessage {...(pickerTitle || messages.headerText)} />
					</Text>
					{showCloseButton && (
						<IconButton
							appearance="subtle"
							icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon')} />}
							label={<FormattedMessage {...messages.closeButtonLabel} />}
							onClick={onClose}
							spacing="compact"
						/>
					)}
				</Flex>

				<CuratedPalette
					analyticsSource={analyticsSource}
					builtInColorMap={builtInColorMap}
					customColorRef={customColorRef}
					onChange={onCuratedColorSelect}
					value={value}
				/>

				{showCustomPalette && (
					<CustomPalette
						analyticsSource={analyticsSource}
						builtInColorMap={builtInColorMap}
						customColorRef={customColorRef}
						onChange={onAddCustomColor}
						onSelectColor={onPreSelectCustomColor}
						value={value}
					/>
				)}

				{(isReadableColor || showCustomPalette) && (
					<ColorValidationMessages
						selectedColor={showCustomPalette ? preSelectedColor || value : value}
						preSelectedColor={preSelectedColor}
						showConfirmation={showConfirmation}
						isReadableColor={isReadableColor}
						validationSuccessMessage={validationSuccessMessage}
						validationFailMessage={validationFailMessage}
					/>
				)}
			</Stack>

			{showResetButton && (
				<Flex alignItems="end" xcss={resetButtonStyles}>
					<Button onClick={onResetClick}>
						<FormattedMessage {...messages.resetButtonText} />
					</Button>
				</Flex>
			)}
		</Flex>
	);
};
